import * as React from "react";
import { Credit, Transaction, BetLog, CreditsRequest, ListBetLogsRequest, ListTransactionsRequest, 
    TransactionSearchType, SportsBet, CreditList, ListBetLogsResponse } from "../../http/protocol";
import { getContainer } from "../../ioc/IOCSetup";
import { CreditService } from "../../services/CreditService";
import { SportBetsService } from "../../services/SportsBetsService";
import { SportsBetLogsService } from "../../services/SportsBetLogsService";
import { WalletService } from "../../services/WalletService";
import { LoadingViewState, RouterView } from "../RouterView";
import {RouteComponentProps, StaticContext, withRouter} from "react-router";
import { FormListener, Form } from "../form/Form";
import { FormSubmitError, FormSubmitSuccess } from "../form/FormFeedback";
import { FormGroup } from "../form/FormGroup";
import { FormInput } from "../form/FormInput";
import { Validators } from "../form/Validators";
import { Submit } from "../form/Submit";

import { EventTimeLine, Event, EventType } from "./EventTimeLine";

import { CreditComponent } from "./CreditView";
import { BetLogComponent } from "../sportsbetting/BetLogView";
import { AttributePanel } from "../AttributePanel";

interface ClawbackReviewState extends LoadingViewState{
    betId? : string;
    bet? : SportsBet;
    validationMap? : any;
    credits : Credit[];
    transactions : Transaction[];
    betLogs : BetLog[];
    mergedData : Event[];
    currentItem : { id: number, type: EventType, data: any};
    isLoading : boolean;
    creditsRequest : CreditsRequest;
    betLogsRequest : ListBetLogsRequest;
    transactionsRequest : ListTransactionsRequest;
    startDateTime? : Date;
    endDateTime? : Date;
    
}

interface ClawbackReviewProps extends RouteComponentProps{
    match : any;
}

export class ClawbackReview extends RouterView<ClawbackReviewProps,ClawbackReviewState> implements FormListener<SportsBet>{
    
    creditService : CreditService;
    walletService : WalletService;
    sportsBetService : SportBetsService;
    sportsBetLogsService : SportsBetLogsService;

    constructor(props : ClawbackReviewProps){
        super(props);
        //if the betId is present in the url, set it to the state.
        let betId = this.props.match.params.id;
        this.creditService = getContainer().getCreditService();
        this.walletService = getContainer().getWalletService();
        this.sportsBetService = getContainer().getSportBetsService();
        this.sportsBetLogsService = getContainer().getSportsBetLogsService();
        let initialListRequest = { limit: 100, offset: 0, orderBy: "id", ascending: true };
        this.state = { betId: betId, bet: { id: betId !== "search"? betId : undefined }, 
            validationMap: {}, credits:[], transactions:[], betLogs:[], mergedData:[], 
            currentItem:{}, isLoading:false, startDateTime: undefined, endDateTime: undefined,
            creditsRequest: initialListRequest, betLogsRequest: initialListRequest ,
            transactionsRequest: Object.assign(initialListRequest, {'type': TransactionSearchType.ATTRIBUTE, 'key': "BET_ID"})
        };
    }

    componentDidMount() {
        if(this.state.betId && this.state.betId !== "search"){
            this.loadBet();
        }
    }

    componentDidUpdate(prevProps : RouteComponentProps, prevState : any) {
        if((this.props.match.params as any).id !== (prevProps.match.params as any).id
            || this.state.isLoading !== prevState.isLoading
        ) {
            this.refreshContent();
        } else {
            super.componentDidUpdate(prevProps, prevState);
        }
    }

    //this function will handle a submit of the form which will have a betId to set its state
    onSubmit(){
        let betId = this.state.betId
        if(betId && betId !== "search"){
            this.setState({betId:betId},()=>{
                this.loadBet();
            });
        }
    }

    formDidUpdate(formModel: SportsBet, validationMap?: any): void {
        let previousBet = this.state.bet!;
        if(previousBet.id !== formModel.id){
            this.setState({betId: formModel.id, bet:formModel, validationMap:validationMap, 
                credits:[], transactions:[], betLogs:[], mergedData:[], currentItem:{}, isLoading:false});
        }
    }
    
    loadContent(): void | Promise<any> {}

    loadBet(): void | Promise<any> {
        let betId = this.state.betId;
        if(betId && betId !== "search"){
            this.setState({isLoading:true},()=>{
                this.sportsBetService.getEntity(betId!).then((bet: SportsBet)=>{
                    //set startDateTime to the bet created date and endDateTime to the bet created date plus 4 days
                    let startDateTime = new Date(bet.created!);
                    let endDateTime = new Date(startDateTime.getTime() + 4*24*60*60*1000);
                    this.setState({bet: bet, startDateTime:startDateTime, endDateTime:endDateTime},()=>{
                        this.loadMoreItems();
                    });
                });
            });
        }
    }

    //This function loads all the credits, transactions and bet logs related to the bet.
    //It then calls a function that merges all the data and sorts it by date and time.
    loadMoreItems(){

        console.log("Clawback review load more items for betId: " + this.state.betId);

        this.setState({isLoading:true},async ()=>{
            let credits = await this.getCredits();
            let transactions = await this.getTransactions();
            let betLogs = await this.getBetLogs();
            this.setState({isLoading: false, credits:credits, transactions:transactions, betLogs:betLogs},()=>{
                this.mergeData();
            });    
        });
    }

    //This function merges all the data filtering out repeated items
    mergeSets(items : any[], source: any[]){
        const seendIds = new Set<number>(source.map((item)=>item.id));
        const uniqueItems = items.filter((item)=>!seendIds.has(item.id));
        return source.concat(uniqueItems) || [];
    }

    getCredits(): Promise<Credit[]>{
        const creditsRequest = Object.assign(this.state.creditsRequest, { reference:this.state.betId ,startDate:this.state.startDateTime, endDate:this.state.endDateTime });
        return this.creditService.list(creditsRequest).then((credits : CreditList)=>{
            return this.mergeSets(credits.items || [], this.state.credits);
        });
    }

    getTransactions(){
        let transactionsRequest = Object.assign(this.state.transactionsRequest, {value: this.state.betId, fromDate: this.state.startDateTime, toDate: this.state.endDateTime});
        return this.walletService.getTransactionList(transactionsRequest).then((transactions)=>{
            return this.mergeSets(transactions.items || [], this.state.transactions);
        });
    }

    getBetLogs(){
        let betLogsRequest = Object.assign(this.state.betLogsRequest, {betId:this.state.betId, startDate:this.state.startDateTime, endDate:this.state.endDateTime});
        return this.sportsBetLogsService.list(betLogsRequest).then((betLogs : ListBetLogsResponse)=>{
            return this.mergeSets(betLogs.items || [], this.state.betLogs);
        });
    }

    parseCredit(credit: Credit): Event {
        return {datetime:new Date(credit.created!), type:EventType.CREDIT, amount:credit.originalAmount, id:credit.id!, userId:credit.userId,
            description: "Credit was generated due to unsettlement failure for bet " + credit.reference
        };
    }

    parseBetLog(betLog: BetLog): Event {
        return {datetime: new Date(betLog.timestamp!), type:EventType.BET_LOG, amount:betLog.risk ? betLog.risk : betLog.payout, id:betLog.id!, userId:betLog.userId,
            description: "Notification received for bet " + betLog.betId + ( betLog.betType ? " of type " + betLog.betType : "") + ". Updated status to " + betLog.status  + 
            (betLog.payout ? " with payout of " + betLog.payout : "")
        };
    }

    parseTransaction(transaction: Transaction, userId: number): Event {
        //Get the amount of the first entry in the transaction if present or set it to 0.
        let amount = transaction.entries!.length > 0 ? transaction.entries![0].amount!.amount : 0; 
        return {datetime: new Date(transaction.timestamp!), type:EventType.TRANSACTION, amount:amount, id:transaction.id!, userId:userId,
            description: "Transaction triggered by " + transaction.comment + " for bet id " + transaction.attributes!["BET_ID"] + " from request " + transaction.attributes!["EXTERNAL_ID"]};
    }

    //This function merges all the data and sorts it by date and time.
    //It then sets the state to the merged data.
    mergeData(){
        let bet = this.state.bet;
        let mergedData : Event[] = [];
        let reducedCredits : Event[] = this.state.credits.map((credit: Credit)=>{ return this.parseCredit(credit); });
        
        let reducedTransactions : Event[] = this.state.transactions.map((transaction: Transaction)=>{
            return this.parseTransaction(transaction, bet!.userId!);
        });
        
        let reducedBetLogs : Event[] = this.state.betLogs.map((betLog: BetLog)=>{ return this.parseBetLog(betLog); });
        
        mergedData = mergedData.concat(reducedCredits).concat(reducedTransactions).concat(reducedBetLogs);
        mergedData.sort((a,b)=>{
            const datetimeComparison = a.datetime.getTime() - b.datetime.getTime();
            if (datetimeComparison !== 0){
                return datetimeComparison;
            }
            if (a.type === b.type){
                return a.id - b.id;
            } else {
                if(a.type === EventType.BET_LOG){
                    return -1;
                } else if (b.type === EventType.BET_LOG){
                    return 1;
                } else if (a.type === EventType.CREDIT){
                    return -1;
                } else if (b.type === EventType.CREDIT){
                    return 1;
                }
                return 0;
            }
                
        });

        let startDate = mergedData.length > 0 ? mergedData[mergedData.length-1].datetime! : new Date();
        startDate = new Date(startDate.getTime() + 1000);
        let endDate = new Date(startDate.getTime() + 172800000);
        this.setState({mergedData:mergedData, isLoading:false, startDateTime:startDate, endDateTime:endDate});
    }

    //This function is used to display the details of the event that was clicked on.
    //It displays the details on the right side of the screen.
    //It also highlights the event that was clicked on.
    displayDetails(event : Event){
        let currentData = {};

        switch(event.type){
            case EventType.CREDIT:
                currentData = this.state.credits.find((credit: Credit)=>{
                    return credit.id === event.id;
                });
                break;
            case EventType.TRANSACTION:
                currentData = this.state.transactions.find((transaction: Transaction)=>{
                    return transaction.id === event.id;
                });
                break;
            case EventType.BET_LOG:
                currentData = this.state.betLogs.find((betLog: BetLog)=>{
                    return betLog.id === event.id;
                });
                break;
            default:
                currentData = {};
        }

        console.log("Clawback review display details for event: " + JSON.stringify(event));

        this.setState({currentItem:{ id: event.id, type: event.type, data: currentData }});
    }

    getTitle(): string {
        return "Clawback Review " + (this.props.match.params.id !== "search" ? this.props.match.params.id : "");
    }

    renderTimeline(){
        return (
        <div className="row">
            <div className="col-md-6">
                <div className="card-box">
                    <div className="ibox-content">
                        <div className="row">
                            <div className="col-md-12">
                                <h4 className="card-title">Timeline</h4>
                                <hr/>
                            </div>
                        </div>
                        <div className="row">
                            <div className="col-md-12">
                                <EventTimeLine 
                                    mergedData={this.state.mergedData} 
                                    displayDetails={(e:Event)=>{ this.displayDetails(e);}} 
                                    loadMoreItems={()=>{ this.loadMoreItems();}} 
                                    isLoading={this.state.isLoading} 
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className="col-md-6">
                <div className="card-box">
                    <div className="ibox-content">
                        <div className="row">
                            <div className="col-md-12">
                                <h4 className="card-title">Event Details</h4>
                                <hr/>
                            </div>
                        </div>
                        <div className="row">
                            <div className="col-md-12">
                                <EventDetails currentItem={this.state.currentItem}/>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        );
    }

    renderContent() {
        return(
            <>
                {this.props.match.params.id === "search" && (
                <div className="row">
                    <div className="col-md-3">
                        <div className={"card"}>
                            <div className="card-body">
                                <Form className="form-horizontal" formListener={this} modelObject={this.state.bet} validationMap={this.state.validationMap}>
                                    <FormSubmitError />
                                    <FormSubmitSuccess text="Gotten review"/>

                                    <FormGroup className="form-group mb-3" model="id">
                                        <label>Bet Id</label>
                                        <div className="row">
                                            <div className="col-md-8">
                                                <FormInput className="form-control" model="id" id="id" type="text" validators={[Validators.required()]}/>
                                            </div>
                                            <div className="col-md-4">
                                                <Submit className="btn btn-primary waves-effect waves-light">Search</Submit>
                                            </div>
                                        </div>
                                    </FormGroup>
                                </Form>
                            </div>
                        </div>
                    </div>
                </div>
                 )}
                {this.state.mergedData.length > 0 && this.renderTimeline()}
            </>
        );
    }

}

export default withRouter(ClawbackReview);

interface EventDetailsProps{
    currentItem : any;
}

//This component displays the details of the event that was clicked on.
//It displays the date and time of the event, the type of event and the amount.
class EventDetails extends React.Component<EventDetailsProps, {}>{
    render(){
        console.log("EventDetails: " + JSON.stringify(this.props.currentItem));
        const currentItem = this.props.currentItem;
        return(
            <div className="row">
                <div className="col-md-12">
                    {
                        currentItem.type === EventType.CREDIT &&
                        <CreditComponent credit={currentItem.data}/>
                    }
                    {
                        currentItem.type === EventType.TRANSACTION &&
                        <AttributePanel header="Transaction Attributes" attributes={currentItem.data.attributes}/>
                    }
                    {
                        currentItem.type === EventType.BET_LOG &&
                        <BetLogComponent betLog={currentItem.data}/>
                    }
                </div>
            </div>
        );
    }
}
